import React, { useState, useEffect } from 'react';
import { Leaf, Heart, PersonStanding, Cat, Flower, X, Dog, TreePalm, Flower2, Apple, Banana, Cherry, Grape, ThumbsDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ServiceType } from '../../types/api';
import BlacklistSection from './BlacklistSection';

interface PreferenceOption {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  group?: 'likes' | 'dislikes' | 'home' | 'allergies' | 'dietary' | 'blacklist';
}

interface PreferencesSectionProps {
  preferences: Record<string, boolean>;
  onChange: (preferences: Record<string, boolean>) => void;
  serviceType?: ServiceType;
}

export const PreferencesSection: React.FC<PreferencesSectionProps> = ({
  preferences = {},
  onChange,
  serviceType = 'flower'
}) => {
  const { t } = useTranslation();
  const [selectedPreferences, setSelectedPreferences] = useState<Record<string, boolean>>(preferences);

  // When external preferences change, update local state
  useEffect(() => {
    setSelectedPreferences(preferences);
  }, [preferences]);

  // Define flower preference options
  const flowerPreferenceOptions: PreferenceOption[] = [
    // Home environment preferences
    { 
      id: 'has_cats', 
      label: t('subscription.preferences.hasCats', 'Has cats at home'),
      description: t('subscription.preferences.hasCatsDesc', 'We will avoid flower varieties that are harmful to cats'),
      icon: <Cat size={18} />,
      group: 'home'
    },
    { 
      id: 'has_dogs', 
      label: t('subscription.preferences.hasDogs', 'Has dogs at home'),
      description: t('subscription.preferences.hasDogsDesc', 'Some flowers can be harmful to dogs'),
      icon: <Dog size={18} />,
      group: 'home'
    },
    { 
      id: 'has_children', 
      label: t('subscription.preferences.hasChildren', 'Has young children'),
      description: t('subscription.preferences.hasChildrenDesc', 'Child-safe arrangements'),
      icon: <PersonStanding size={18} />,
      group: 'home'
    },
    
    // Blacklist preferences - never include these
    { 
      id: 'blacklist_roses', 
      label: t('subscription.preferences.blacklistRoses', 'Never include roses'),
      description: t('subscription.preferences.blacklistRosesDesc', 'Absolutely no roses in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_chrysanthemums', 
      label: t('subscription.preferences.blacklistChrysanthemums', 'Never include chrysanthemums'),
      description: t('subscription.preferences.blacklistChrysanthemumsDesc', 'Absolutely no chrysanthemums in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_lilies', 
      label: t('subscription.preferences.blacklistLilies', 'Never include lilies'),
      description: t('subscription.preferences.blacklistLiliesDesc', 'Absolutely no lilies in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_carnations', 
      label: t('subscription.preferences.blacklistCarnations', 'Never include carnations'),
      description: t('subscription.preferences.blacklistCarnationsDesc', 'Absolutely no carnations in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_sunflowers', 
      label: t('subscription.preferences.blacklistSunflowers', 'Never include sunflowers'),
      description: t('subscription.preferences.blacklistSunflowersDesc', 'Absolutely no sunflowers in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_red_flowers', 
      label: t('subscription.preferences.blacklistRedFlowers', 'Never include red flowers'),
      description: t('subscription.preferences.blacklistRedFlowersDesc', 'Absolutely no red-colored flowers in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_white_flowers', 
      label: t('subscription.preferences.blacklistWhiteFlowers', 'Never include white flowers'),
      description: t('subscription.preferences.blacklistWhiteFlowersDesc', 'Absolutely no white-colored flowers in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_scented_flowers', 
      label: t('subscription.preferences.blacklistScentedFlowers', 'Never include scented flowers'),
      description: t('subscription.preferences.blacklistScentedFlowersDesc', 'Absolutely no flowers with strong scents'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    
    // Likes preferences
    { 
      id: 'likes_green_leaves', 
      label: t('subscription.preferences.likesGreenLeaves', 'Likes green leaves'),
      description: t('subscription.preferences.likesGreenLeavesDesc', 'Include more foliage'),
      icon: <Leaf size={18} />,
      group: 'likes'
    },
    { 
      id: 'likes_roses', 
      label: t('subscription.preferences.likesRoses', 'Likes roses'),
      description: t('subscription.preferences.likesRosesDesc', 'Include more roses when available'),
      icon: <Flower2 size={18} />,
      group: 'likes'
    },
    { 
      id: 'likes_wildflowers', 
      label: t('subscription.preferences.likesWildflowers', 'Likes wildflowers'),
      description: t('subscription.preferences.likesWildflowersDesc', 'Include wildflower varieties'),
      icon: <Flower size={18} />,
      group: 'likes'
    },
    { 
      id: 'likes_tropical', 
      label: t('subscription.preferences.likesTropical', 'Likes tropical'),
      description: t('subscription.preferences.likesTropicalDesc', 'Include tropical varieties'),
      icon: <TreePalm size={18} />,
      group: 'likes'
    },
    { 
      id: 'likes_pastels', 
      label: t('subscription.preferences.likesPastels', 'Likes pastel colors'),
      description: t('subscription.preferences.likesPastelsDesc', 'Prefer softer color palettes'),
      icon: <Flower size={18} />,
      group: 'likes'
    },
    
    // Dislikes preferences
    { 
      id: 'dislikes_chrysanthemums', 
      label: t('subscription.preferences.dislikesChrysanthemums', 'Dislikes chrysanthemums'),
      description: t('subscription.preferences.dislikesChrysanthemumsDesc', 'Avoid chrysanthemums'),
      icon: <X size={18} />,
      group: 'dislikes'
    },
    { 
      id: 'dislikes_strong_scents', 
      label: t('subscription.preferences.dislikesStrongScents', 'Dislikes strong scents'),
      description: t('subscription.preferences.dislikesStrongScentsDesc', 'Avoid strongly scented flowers'),
      icon: <X size={18} />,
      group: 'dislikes'
    },
    { 
      id: 'dislikes_bright_colors', 
      label: t('subscription.preferences.dislikesBrightColors', 'Dislikes bright colors'),
      description: t('subscription.preferences.dislikesBrightColorsDesc', 'Avoid bright or bold colors'),
      icon: <X size={18} />,
      group: 'dislikes'
    }
  ];

  // Define fruit preference options
  const fruitPreferenceOptions: PreferenceOption[] = [
    // Home environment preferences
    { 
      id: 'has_children', 
      label: t('subscription.fruitPreferences.hasChildren', 'Has young children'),
      description: t('subscription.fruitPreferences.hasChildrenDesc', 'Include kid-friendly fruits with easy-to-eat options'),
      icon: <PersonStanding size={18} />,
      group: 'home'
    },
    { 
      id: 'elderly_friendly', 
      label: t('subscription.fruitPreferences.elderlyFriendly', 'Elderly-friendly options'),
      description: t('subscription.fruitPreferences.elderlyFriendlyDesc', 'Prefer softer fruits that are easier to eat'),
      icon: <Heart size={18} />,
      group: 'home'
    },
    
    // Blacklist preferences - never include these
    { 
      id: 'blacklist_citrus', 
      label: t('subscription.fruitPreferences.blacklistCitrus', 'Never include citrus fruits'),
      description: t('subscription.fruitPreferences.blacklistCitrusDesc', 'Absolutely no citrus fruits in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_tropical', 
      label: t('subscription.fruitPreferences.blacklistTropical', 'Never include tropical fruits'),
      description: t('subscription.fruitPreferences.blacklistTropicalDesc', 'Absolutely no tropical fruits in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_berries', 
      label: t('subscription.fruitPreferences.blacklistBerries', 'Never include berries'),
      description: t('subscription.fruitPreferences.blacklistBerriesDesc', 'Absolutely no berries in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_stone_fruits', 
      label: t('subscription.fruitPreferences.blacklistStoneFruits', 'Never include stone fruits'),
      description: t('subscription.fruitPreferences.blacklistStoneFruitsDesc', 'Absolutely no stone fruits (peaches, plums, etc.) in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_apples', 
      label: t('subscription.fruitPreferences.blacklistApples', 'Never include apples'),
      description: t('subscription.fruitPreferences.blacklistApplesDesc', 'Absolutely no apples in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_bananas',  
      label: t('subscription.fruitPreferences.blacklistBananas', 'Never include bananas'),
      description: t('subscription.fruitPreferences.blacklistBananasDesc', 'Absolutely no bananas in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_melons', 
      label: t('subscription.fruitPreferences.blacklistMelons', 'Never include melons'),
      description: t('subscription.fruitPreferences.blacklistMelonsDesc', 'Absolutely no melons (watermelon, cantaloupe, etc.) in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    { 
      id: 'blacklist_grapes', 
      label: t('subscription.fruitPreferences.blacklistGrapes', 'Never include grapes'),
      description: t('subscription.fruitPreferences.blacklistGrapesDesc', 'Absolutely no grapes in any delivery'),
      icon: <X size={18} />,
      group: 'blacklist'
    },
    
    // Dietary preferences
    { 
      id: 'organic_preferred', 
      label: t('subscription.fruitPreferences.organicPreferred', 'Prefers organic fruits'),
      description: t('subscription.fruitPreferences.organicPreferredDesc', 'Prioritize organic fruit varieties when available'),
      icon: <Leaf size={18} />,
      group: 'dietary'
    },
    { 
      id: 'diabetic_friendly', 
      label: t('subscription.fruitPreferences.diabeticFriendly', 'Diabetic-friendly selection'),
      description: t('subscription.fruitPreferences.diabeticFriendlyDesc', 'Focus on lower sugar fruits and berries'),
      icon: <Heart size={18} />,
      group: 'dietary'
    },
    
    // Fruit type preferences
    { 
      id: 'likes_tropical_fruits', 
      label: t('subscription.fruitPreferences.likesTropicalFruits', 'Loves tropical fruits'),
      description: t('subscription.fruitPreferences.likesTropicalFruitsDesc', 'Include more mangoes, pineapples, dragon fruit, etc.'),
      icon: <TreePalm size={18} />,
      group: 'likes'
    },
    { 
      id: 'likes_citrus_fruits', 
      label: t('subscription.fruitPreferences.likesCitrusFruits', 'Loves citrus fruits'),
      description: t('subscription.fruitPreferences.likesCitrusFruitsDesc', 'Include more oranges, lemons, limes, grapefruits'),
      icon: <Cherry size={18} />,
      group: 'likes'
    },
    { 
      id: 'likes_berries', 
      label: t('subscription.fruitPreferences.likesBerries', 'Loves berries'),
      description: t('subscription.fruitPreferences.likesBerriesDesc', 'Include strawberries, blueberries, raspberries when available'),
      icon: <Grape size={18} />,
      group: 'likes'
    },
    { 
      id: 'likes_stone_fruits', 
      label: t('subscription.fruitPreferences.likesStoneFruits', 'Loves stone fruits'),
      description: t('subscription.fruitPreferences.likesStoneFruitsDesc', 'Include peaches, plums, apricots, cherries'),
      icon: <Cherry size={18} />,
      group: 'likes'
    },
    { 
      id: 'likes_apples_pears', 
      label: t('subscription.fruitPreferences.likesApplesPears', 'Loves apples & pears'),
      description: t('subscription.fruitPreferences.likesApplesPearsDesc', 'Include various apple and pear varieties'),
      icon: <Apple size={18} />,
      group: 'likes'
    },
    { 
      id: 'likes_exotic_fruits', 
      label: t('subscription.fruitPreferences.likesExoticFruits', 'Loves exotic fruits'),
      description: t('subscription.fruitPreferences.likesExoticFruitsDesc', 'Include unique varieties like passion fruit, lychee, rambutan'),
      icon: <Banana size={18} />,
      group: 'likes'
    },
    
    // Dislikes preferences
    { 
      id: 'dislikes_very_sweet', 
      label: t('subscription.fruitPreferences.dislikesVerySweet', 'Dislikes very sweet fruits'),
      description: t('subscription.fruitPreferences.dislikesVerySweetDesc', 'Avoid overly sweet fruits, prefer tart or balanced flavors'),
      icon: <ThumbsDown size={18} />,
      group: 'dislikes'
    },
    { 
      id: 'dislikes_soft_texture', 
      label: t('subscription.fruitPreferences.dislikesSoftTexture', 'Dislikes soft-textured fruits'),
      description: t('subscription.fruitPreferences.dislikesSoftTextureDesc', 'Avoid overly ripe or mushy fruits, prefer crisp textures'),
      icon: <ThumbsDown size={18} />,
      group: 'dislikes'
    },
    { 
      id: 'dislikes_seeds', 
      label: t('subscription.fruitPreferences.dislikesSeeds', 'Dislikes fruits with many seeds'),
      description: t('subscription.fruitPreferences.dislikesSeedsDesc', 'Avoid fruits with lots of seeds like guava, passion fruit'),
      icon: <ThumbsDown size={18} />,
      group: 'dislikes'
    },
    
    // Allergies
    { 
      id: 'allergic_to_citrus', 
      label: t('subscription.fruitPreferences.allergicToCitrus', 'Allergic to citrus'),
      description: t('subscription.fruitPreferences.allergicToCitrusDesc', 'Avoid all citrus fruits'),
      icon: <X size={18} />,
      group: 'allergies'
    },
    { 
      id: 'allergic_to_stone_fruits', 
      label: t('subscription.fruitPreferences.allergicToStoneFruits', 'Allergic to stone fruits'),
      description: t('subscription.fruitPreferences.allergicToStoneFruitsDesc', 'Avoid peaches, plums, apricots, cherries'),
      icon: <X size={18} />,
      group: 'allergies'
    }
  ];

  // Choose preference options based on service type
  const preferenceOptions = serviceType === 'fruit' ? fruitPreferenceOptions : flowerPreferenceOptions;



  // Filter out blacklist group from regular preferences
  const filteredPreferenceOptions = (serviceType === 'fruit' ? fruitPreferenceOptions : flowerPreferenceOptions)
    .filter(option => option.group !== 'blacklist');

  // Group preferences by category (excluding blacklist)
  const groupedPreferences = filteredPreferenceOptions.reduce((groups, option) => {
    const group = option.group || 'other';
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(option);
    return groups;
  }, {} as Record<string, PreferenceOption[]>);

  // Define the order of groups with blacklist at the bottom
  const groupOrder = serviceType === 'fruit' 
    ? ['home', 'allergies', 'dietary', 'likes', 'dislikes', 'other']
    : ['home', 'allergies', 'likes', 'dislikes', 'other'];
  
  // Sort groups based on the defined order
  const sortedGroups = groupOrder
    .filter(groupName => groupedPreferences[groupName])
    .map(groupName => [groupName, groupedPreferences[groupName]] as [string, PreferenceOption[]]);

  // Handle preference toggle
  const handlePreferenceToggle = (preferenceId: string) => {
    const updatedPreferences = {
      ...selectedPreferences,
      [preferenceId]: !selectedPreferences[preferenceId]
    };
    
    if (!updatedPreferences[preferenceId]) {
      // Remove the preference if it's being set to false
      delete updatedPreferences[preferenceId];
    }
    
    setSelectedPreferences(updatedPreferences);
    onChange(updatedPreferences);
  };

  // Get translation for group names based on service type
  const getGroupTitle = (group: string): string => {
    if (serviceType === 'fruit') {
      switch (group) {
        case 'home':
          return t('subscription.fruitPreferences.groupHome', 'Household Considerations');
        case 'dietary':
          return t('subscription.fruitPreferences.groupDietary', 'Dietary Preferences');
        case 'blacklist':
          return t('subscription.fruitPreferences.groupBlacklist', 'Never Include (Blacklist)');
        case 'likes':
          return t('subscription.fruitPreferences.groupLikes', 'Fruit Preferences');
        case 'dislikes':
          return t('subscription.fruitPreferences.groupDislikes', 'Dislikes');
        case 'allergies':
          return t('subscription.fruitPreferences.groupAllergies', 'Allergies');
        default:
          return t('subscription.fruitPreferences.groupOther', 'Other Preferences');
      }
    } else {
      switch (group) {
        case 'home': 
          return t('subscription.preferences.groupHome', 'Home Environment');
        case 'blacklist':
          return t('subscription.preferences.groupBlacklist', 'Never Include (Blacklist)');
        case 'likes':
          return t('subscription.preferences.groupLikes', 'Flower Preferences');
        case 'dislikes':
          return t('subscription.preferences.groupDislikes', 'Dislikes');
        case 'allergies':
          return t('subscription.preferences.groupAllergies', 'Allergies');
        default:
          return t('subscription.preferences.groupOther', 'Other Preferences');
      }
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-medium text-gray-900 mb-2">
          {serviceType === 'fruit' 
            ? t('subscription.fruitPreferences.title', 'Fruit Preferences')
            : t('subscription.preferences.title', 'Flower Preferences')}
        </h2>
        <p className="text-sm text-gray-600">
          {serviceType === 'fruit'
            ? t('subscription.fruitPreferences.subtitle', 'Tell us your preferences to help us select the best fruits for you')
            : t('subscription.preferences.subtitle', 'Tell us your preferences to help us create better arrangements for you')}
        </p>
      </div>

      {/* Render preferences by group in specified order */}
      {sortedGroups.map(([group, options]) => (
        <div key={group} className="space-y-3">
          <h3 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
            {getGroupTitle(group)}
          </h3>
          
          <div className="space-y-2">
            {options.map((option) => (
              <div 
                key={option.id}
                className={`border rounded-lg p-3 cursor-pointer transition-all duration-200 ${
                  selectedPreferences[option.id]
                    ? 'border-yellow-400 bg-yellow-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                onClick={() => handlePreferenceToggle(option.id)}
              >
                <div className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 p-2 rounded-full ${
                    selectedPreferences[option.id]
                      ? 'bg-yellow-100 text-yellow-600'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {option.icon}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">
                        {option.label}
                      </h4>
                      <input
                        type="checkbox"
                        checked={selectedPreferences[option.id] || false}
                        onChange={() => handlePreferenceToggle(option.id)}
                        className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {option.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Blacklist Section */}
      <BlacklistSection
        preferences={selectedPreferences}
        onChange={(newPreferences) => {
          setSelectedPreferences(newPreferences);
          onChange(newPreferences);
        }}
        serviceType={serviceType}
      />
    </div>
  );
};

export default PreferencesSection; 